"use client";
import { useState } from "react";
import { Button } from "@ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@ui/card";
import { Badge } from "@ui/badge";
import { Separator } from "@ui/separator";
import {
  MapPin,
  Clock,
  Users,
  Mail,
  Phone,
  Calendar,
  BookOpen,
  GraduationCap,
} from "lucide-react";
import { jobListings } from "./data";

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-background mt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-nature-gradient">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-gradient">Join Our Team</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
              Shape the future of natural medicine education at OICNM
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>Remote & On-site Positions</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Flexible Teaching</span>
              </div>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                <span>Educational Excellence</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Vacancies</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              OICNM invites applications for the following remote and on-site
              positions
            </p>
          </div>

          <div className="grid gap-8 max-w-4xl mx-auto">
            {jobListings.map((job, index) => (
              <Card
                key={job.id}
                className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/20"
              >
                <CardHeader className="pb-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        {job.icon}
                      </div>
                      <div>
                        <CardTitle className="text-xl mb-2">
                          {job.title}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              job.type.includes("Remote")
                                ? "default"
                                : "secondary"
                            }
                          >
                            <MapPin className="w-3 h-3 mr-1" />
                            {job.type}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant={selectedJob === job.id ? "default" : "outline"}
                      size="sm"
                      onClick={() =>
                        setSelectedJob(selectedJob === job.id ? null : job.id)
                      }
                    >
                      {selectedJob === job.id ? "Hide Details" : "View Details"}
                    </Button>
                  </div>
                </CardHeader>

                <CardContent>
                  <CardDescription className="text-base mb-4 leading-relaxed">
                    {job.summary}
                  </CardDescription>

                  {selectedJob === job.id && (
                    <div className="space-y-6 animate-in slide-in-from-top-2 duration-300">
                      <Separator />

                      <div>
                        <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                          <BookOpen className="w-4 h-4" />
                          Requirements
                        </h4>
                        <ul className="space-y-2">
                          {job.requirements.map((req, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-muted-foreground"
                            >
                              <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Responsibilities
                        </h4>
                        <ul className="space-y-2">
                          {job.responsibilities.map((resp, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-muted-foreground"
                            >
                              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" />
                              <span>{resp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Section */}
      <section className="py-16 bg-wisdom-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-solid border-2 border-primary/20">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl md:text-3xl mb-4">
                  How to Apply
                </CardTitle>
                <CardDescription className="text-lg">
                  Ready to join our dynamic team? Here&apos;s how to get
                  started.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-8">
                <div className="prose prose-lg max-w-none text-muted-foreground">
                  <p className="text-center text-base leading-relaxed">
                    If you are a motivated and experienced individual looking to
                    join a dynamic team, please submit your application to OICNM
                    via <strong>careers@theoicnm.com</strong> by{" "}
                    <strong>September 22, 2025</strong>. Include your CV, cover
                    letter, and relevant certificates.
                  </p>
                </div>

                <Separator />

                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg mb-4">
                      Application Deadline
                    </h4>
                    <div className="flex items-center gap-3 p-4 bg-destructive/10 rounded-lg border border-solid border-destructive/20">
                      <Calendar className="w-5 h-5 text-destructive" />
                      <div>
                        <p className="font-medium text-destructive">
                          September 22, 2025
                        </p>
                        <p className="text-sm text-destructive/80">
                          Don&apos;t miss this opportunity!
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg mb-4">
                      Contact Information
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-primary" />
                        <span className="text-sm">careers@theoicnm.com</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-primary" />
                        <span className="text-sm">(+234) 9069 730 290</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
